import React from 'react';
import { AceInvitationCloudStatusEnum } from 'packages/cosell/src/types/enums';
import {
  CalendarClockIcon,
  SyncedIcon,
  WarningIcon,
} from 'packages/cosell/assets';
import { Divider } from '@tackle-io/platform-ui';
import { formatDate } from 'packages/cosell/src/utilities/formatDate';
import { toDateTime } from 'utils/dates';

export interface RightRailContentConfig {
  title: string;
  icon?: JSX.Element;
  //* Intro text is require is important when we want text before a subIcon & title */
  introText?: string;
  guidanceText: string | React.ReactNode;
  subTitle?: string | JSX.Element | null;
  subIcon?: JSX.Element;
}

// TITLES CONSTANTS
export const NEW_INVITATION = 'New invitation';
const PENDING_CLOUD_ACCEPTANCE = 'Pending cloud acceptance';
const PENDING_CLOUD_REJECTION = 'Pending cloud rejection';
export const INVITATION_ACCEPTED = 'Invitation accepted';
export const INVITATION_REJECTED = 'Invitation rejected';
export const INVITATION_EXPIRED = 'Invitation expired';
export const CLOUD_CONTACTS_TITLE = 'Cloud contacts';

// GUIDANCE TEXTS CONSTANTS
export const NEW_AWS_INVITATION_INTRO_TEXT =
  'You’ve been invited to co-sell with your AWS cloud partner! Accept it to view all the invitation details and start co-selling with your cloud partner, or reject the invitation.';

export const NEW_AWS_INVITATION_GUIDANCE_TEXT =
  'AWS automatically rejects invitations that are not accepted by the expiration time.';

export const NEW_PARTNER_INVITATION_INTRO_TEXT =
  'Your invitation to co-sell has been shared with AWS! We will let you know when they accept or reject it.';

export const NEW_PARTNER_INVITATION_GUIDANCE_TEXT =
  'When AWS accepts an invitation, they will update the status of the corresponding opportunity to “approved”. When AWS rejects an invitation, they will update the status of the corresponding opportunity to “rejected”.';

export const NO_CLOUD_CONTACT_GUIDANCE_TEXT =
  'You don’t have any cloud contacts yet. Your cloud partner will share them in the corresponding co-sell opportunity after this co-sell invitation has been accepted.';

const PENDING_INVITATION_ACCEPTED_GUIDANCE_TEXT =
  "This invitation was accepted for co-selling! It might take a few minutes for AWS to create the opportunity. When it's created, you can view it from your list of opportunities in Tackle.";

const PENDING_INVITATION_REJECTED_GUIDANCE_TEXT =
  'This invitation was rejected. It might take a few minutes for AWS to reject the opportunity.';

export const AWS_INVITATION_ACCEPTED_GUIDANCE_TEXT =
  'This invitation was accepted for co-selling!';

export const AWS_INVITATION_REJECTED_GUIDANCE_TEXT =
  'This invitation was rejected for the reason above. You can contact your cloud partner to follow up.';

export const PARTNER_INVITATION_ACCEPTED_GUIDANCE_TEXT =
  'This invitation was accepted by AWS for co-selling! You can continue tracking and working the related opportunity by selecting it below.';

export const PARTNER_INVITATION_REJECTED_GUIDANCE_TEXT =
  'This invitation was rejected by AWS for the reason above. You can contact your cloud partner to follow up.';

const InvitationExpired = ({ expirationDate }) => (
  <span>
    <b>This invitation expired on {formatDate(expirationDate)}.</b> AWS
    automatically rejects invitations that are not accepted by the expiration
    date.
  </span>
);

const getPendingGuidanceText = ({
  pendingOperationId,
}: {
  pendingOperationId: 'acceptInvitation' | 'rejectInvitation' | null;
}) => {
  if (pendingOperationId === 'acceptInvitation') {
    return PENDING_INVITATION_ACCEPTED_GUIDANCE_TEXT;
  } else if (pendingOperationId === 'rejectInvitation') {
    return PENDING_INVITATION_REJECTED_GUIDANCE_TEXT;
  }
  return NEW_AWS_INVITATION_GUIDANCE_TEXT;
};

const getPendingTitle = (
  pendingOperation: 'acceptInvitation' | 'rejectInvitation' | null,
) => {
  if (pendingOperation === 'acceptInvitation') {
    return PENDING_CLOUD_ACCEPTANCE;
  } else if (pendingOperation === 'rejectInvitation') {
    return PENDING_CLOUD_REJECTION;
  }

  return NEW_INVITATION;
};

export const getRightRailContentConfig = ({
  status,
  rejectionReason,
  pendingOperationId,
  expirationDate,
  opportunityId,
  origin,
}: {
  status: AceInvitationCloudStatusEnum;
  rejectionReason?: string | null;
  pendingOperationId: 'acceptInvitation' | 'rejectInvitation' | null;
  expirationDate?: string;
  opportunityId?: string | null;
  origin: 'aws' | 'partner';
}): RightRailContentConfig => {
  const expirationTime = toDateTime(expirationDate)
    .toLocal()
    .toFormat('hh:mm a ZZZZ');
  // handle default status configuration
  const config: Record<AceInvitationCloudStatusEnum, RightRailContentConfig> = {
    [AceInvitationCloudStatusEnum.PENDING]: {
      title: getPendingTitle(pendingOperationId),
      icon: <SyncedIcon />,
      introText:
        origin === 'aws'
          ? NEW_AWS_INVITATION_INTRO_TEXT
          : NEW_PARTNER_INVITATION_INTRO_TEXT,
      subIcon: origin === 'aws' ? <CalendarClockIcon /> : undefined,
      subTitle:
        origin === 'aws' ? (
          <span>
            Expires on {formatDate(expirationDate)} <br></br>at {expirationTime}
          </span>
        ) : undefined,
      guidanceText: (
        <>
          <div
            data-testid="guidance-text"
            style={{
              marginTop: origin === 'aws' ? '16px' : '24px',
              marginBottom: '16px',
            }}
          >
            {origin === 'aws'
              ? getPendingGuidanceText({ pendingOperationId })
              : NEW_PARTNER_INVITATION_GUIDANCE_TEXT}
          </div>
          {opportunityId && (
            <>
              <Divider style={{ margin: '16px 0' }} />
              <div>
                <b>Related Opportunity:</b> {opportunityId}
              </div>
            </>
          )}
        </>
      ),
    },
    [AceInvitationCloudStatusEnum.ACCEPTED]: {
      title: INVITATION_ACCEPTED,
      icon: <SyncedIcon />,
      guidanceText: (
        <>
          <div
            data-testid="guidance-text"
            style={{
              marginTop: origin === 'aws' ? '16px' : '24px',
              marginBottom: '16px',
            }}
          >
            {origin === 'aws'
              ? AWS_INVITATION_ACCEPTED_GUIDANCE_TEXT
              : PARTNER_INVITATION_ACCEPTED_GUIDANCE_TEXT}
          </div>
          {opportunityId && (
            <>
              <Divider style={{ margin: '16px 0' }} />
              <div>
                <b>Related Opportunity:</b> {opportunityId}
              </div>
            </>
          )}
        </>
      ),
    },
    [AceInvitationCloudStatusEnum.REJECTED]: {
      title: INVITATION_REJECTED,
      icon: <SyncedIcon />,
      guidanceText: (
        <>
          <div
            data-testid="guidance-text"
            style={{
              marginTop: origin === 'aws' ? '16px' : '24px',
              marginBottom: '16px',
            }}
          >
            {origin === 'aws'
              ? AWS_INVITATION_REJECTED_GUIDANCE_TEXT
              : PARTNER_INVITATION_REJECTED_GUIDANCE_TEXT}
          </div>
          {opportunityId && (
            <>
              <Divider style={{ margin: '16px 0' }} />
              <div>
                <b>Related Opportunity:</b> {opportunityId}
              </div>
            </>
          )}
        </>
      ),
      subIcon: <WarningIcon />,
      subTitle: rejectionReason,
    },
    [AceInvitationCloudStatusEnum.EXPIRED]: {
      title: INVITATION_EXPIRED,
      icon: <SyncedIcon />,
      guidanceText: (
        <>
          <div
            data-testid="guidance-text"
            style={{
              marginTop: origin === 'aws' ? '16px' : '24px',
              marginBottom: '16px',
            }}
          >
            {origin === 'aws' ? (
              <InvitationExpired expirationDate={expirationDate} />
            ) : (
              NEW_PARTNER_INVITATION_GUIDANCE_TEXT
            )}
          </div>
          {opportunityId && (
            <>
              <Divider style={{ margin: '16px 0' }} />
              <div>
                <b>Related Opportunity:</b> {opportunityId}
              </div>
            </>
          )}
        </>
      ),
    },
  };

  return config[status];
};

// the cloud partner will share cloud contacts after accepted or reject the Invitation
export const shouldRenderCloudContact = (
  status: AceInvitationCloudStatusEnum,
): boolean => {
  const acceptedOrRejected = [
    AceInvitationCloudStatusEnum.REJECTED,
    AceInvitationCloudStatusEnum.ACCEPTED,
  ];
  return acceptedOrRejected.includes(status);
};
